import clsx from 'clsx'
import Clickable from 'components/Common/Clickable'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'LuxKit/Divider'
import LineChairWithAirplaneIcon from 'LuxKit/Icons/line/LineChairWithAirplaneIcon'
import LineTaxiIcon from 'LuxKit/Icons/line/LineTaxiIcon'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import TextLink from 'LuxKit/TextLink'
import Tooltip from 'LuxKit/Tooltip'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import Group from 'components/utils/Group'
import interactiveFocusStyle from 'styles/utils/interactiveFocusStyle'
import { tierGradientBackgroundStyle, tierNormalContrastBackgroundStyle, tierNormalContrastColorStyle } from 'styles/utils/tierStyles'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import withMountGuard from 'hocs/withMountGuard'
import { useAppSelector } from 'hooks/reduxHooks'
import { arrayToObject, nonNullable } from 'lib/array/arrayUtils'
import { clamp } from 'lib/maths/mathUtils'
import LoyaltyAccountTileCircularProgressBar from 'loyalty/components/LoyaltyAccountTileCircularProgressBar'
import LoyaltyProgramNameLogo from 'loyalty/components/LoyaltyProgramNameLogo'
import LoyaltyDrawerTierCard from 'loyalty/components/LoyaltyDrawerTierCard'
import LoyaltyDrawerCongratulationsBanner from 'loyalty/components/LoyaltyDrawerCongratulationsBanner'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import useCurrentLoyaltyPendingPoints from 'loyalty/hooks/useCurrentLoyaltyPendingPoints'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import { rem } from 'polished'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import AccountDrawerBenefitHighlightItem from './AccountDrawerBenefitHighlightItem'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import LineCrownIcon from 'LuxKit/Icons/line/LineCrownIcon'
import LineShieldCheckIcon from 'LuxKit/Icons/line/LineShieldCheckIcon'
import CopyToClipboardClickableIcon from 'checkout/Components/Confirmation/BookingDetailsV2/CopyToClipboardClickableIcon'

const Container = styled(Clickable)`
  padding: ${rem(12)} ${rem(16)};
  color: ${props => props.theme.palette.product.luxPlus.contrast};
  border-radius: ${props => props.theme.borderRadius.M};
  overflow: hidden;
  ${tierGradientBackgroundStyle}
  ${tierNormalContrastColorStyle}

  &:focus {
    ${interactiveFocusStyle}
  }
`

const ProgressBarsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  align-self: stretch;
  padding-bottom: ${rem(4)};
`

const InfoIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  line-height: 1;
`

const TierDivider = styled(Divider)`
  opacity: 0.5;
  ${tierNormalContrastBackgroundStyle}
`

const TIER_BENEFITS: Record<App.LoyaltyTier, Array<App.LoyaltyBenefitType>> = {
  bronze: [],
  silver: ['hotel_upgrade', 'lounge_pass', 'discounted_insurance'],
  gold: ['hotel_upgrade', 'airport_transfer', 'lounge_pass'],
  platinum: ['airport_transfer', 'hotel_upgrade', 'lounge_pass'],
}

const BENEFIT_CONFIG: Utils.PartialRecord<App.LoyaltyBenefitType, {
  title: string
  icon: React.ReactElement
}> = {
  lounge_pass: {
    title: 'Free airport lounge passes',
    icon: <LineChairWithAirplaneIcon />,
  },
  airport_transfer: {
    title: 'Free airport chauffeur service',
    icon: <LineTaxiIcon />,
  },
  hotel_upgrade: {
    title: 'Free room or package upgrade',
    icon: <LineCrownIcon />,
  },
  discounted_insurance: {
    title: 'Discounted travel insurance',
    icon: <LineShieldCheckIcon />,
  },
}

interface BenefitConfigItem {
  code: App.LoyaltyBenefitType
  displayInfo: {
    title: string
    icon: React.ReactElement
  }
}

function getTierBenefitDisplayInfo(tier: App.LoyaltyTier): Array<BenefitConfigItem> {
  const tierBenefits = TIER_BENEFITS[tier]
  const displayInfoItems = tierBenefits.map(code => {
    const displayInfo = BENEFIT_CONFIG[code]
    return displayInfo ? { code, displayInfo } : undefined
  })
  return nonNullable(displayInfoItems)
}

interface Props {
  onLinkClick: () => void
  loyaltyAccountData: App.LoyaltyAccount
}

function AccountMenuLoyaltyHasAccountDetailsTile({ onLinkClick, loyaltyAccountData }: Props) {
  const tier = loyaltyAccountData.tier
  const benefits = loyaltyAccountData.benefits
  const points = loyaltyAccountData.points
  const membershipNumber = `${loyaltyAccountData.accountNumber}`

  const [, , nextTier] = useCurrentLoyaltyTier()
  const [pendingPoints] = useCurrentLoyaltyPendingPoints()

  const benefitsMap = arrayToObject(benefits, benefit => benefit.code)
  const tierBenefitsDisplayInfo = getTierBenefitDisplayInfo(tier)

  const validBenefitsDisplayInfo = nonNullable(
    tierBenefitsDisplayInfo.map(displayInfo =>
      benefitsMap[displayInfo.code] ? displayInfo : undefined,
    ),
  )

  const handleCopyClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault()
    event.stopPropagation()
  }, [])

  // Tier progression mode (platinum users are in retain mode)
  const isRetainMode = !nextTier
  const unlockCardTier = nextTier ?? tier

  // Current progress values
  const currentStatusCredits = loyaltyAccountData.statusCredits
  const currentNights = loyaltyAccountData.numberOfNights

  // Target values based on mode (retain vs upgrade)
  const targetCredits = isRetainMode
    ? loyaltyAccountData.totalCreditsForTierRetention
    : loyaltyAccountData.totalCreditsForTierUpgrade
  const targetNights = isRetainMode
    ? loyaltyAccountData.numberOfNightsForTierRetention
    : loyaltyAccountData.numberOfNightsForTierUpgrade

  // Remaining values to reach target
  const creditsRemaining = Math.max(targetCredits - currentStatusCredits, 0)
  const nightsRemaining = Math.max(targetNights - currentNights, 0)

  // Platinum user has retained their tier if they've met all requirements
  const hasRetainedTier = isRetainMode && creditsRemaining === 0 && nightsRemaining === 0

  // Calculate progress for circular bars
  const statusCreditsProgress = useMemo(() => {
    if (targetCredits === 0) return 100
    const baseProgress = Math.floor((currentStatusCredits / targetCredits) * 100)
    return clamp(0, baseProgress, 100)
  }, [currentStatusCredits, targetCredits])

  const nightsProgress = useMemo(() => {
    if (targetNights === 0) return 100
    const baseProgress = Math.floor((currentNights / targetNights) * 100)
    return clamp(0, baseProgress, 100)
  }, [currentNights, targetNights])

  return <ProductPaletteProvider palette={`tier-${tier}`}>
    <Container
      to={`/${LOYALTY_LANDING_PAGE}`}
      onClick={onLinkClick}
      className={clsx(`tier-${tier}`)}
    >
      <VerticalSpacer gap={20}>
        <VerticalSpacer gap={4}>
          <Group direction="horizontal" horizontalAlign="space-between">
            <LoyaltyProgramNameLogo width={72} />
            {tier && <BodyText variant="medium" format="uppercase">{getLoyaltyTierDisplayName(tier)}</BodyText>}
          </Group>
          <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end">
            <Group direction="horizontal" gap={4} verticalAlign="center">
              <Caption variant="large">Membership {membershipNumber}</Caption>
              <CopyToClipboardClickableIcon
                value={membershipNumber}
                onCopyClick={handleCopyClick}
                ariaLabel="Copy membership number"
              />
            </Group>
            <Group direction="horizontal" gap={4} verticalAlign="center">
              <Heading variant="heading6">
                <FormatDecimal value={points} />
              </Heading>
              <Caption variant="medium" weight="normal">pts</Caption>
            </Group>
          </Group>
        </VerticalSpacer>

        {/* Circular progress bars */}
        <ProgressBarsContainer>
          <Group direction="vertical" horizontalAlign="center" gap={4}>
            <LoyaltyAccountTileCircularProgressBar
              progress={statusCreditsProgress}
              tier={tier}
            >
              <Group direction="vertical" horizontalAlign="center" gap={2}>
                <BodyText variant="medium" weight="bold">
                  <FormatDecimal value={currentStatusCredits} />
                </BodyText>
                <TierDivider className={`tier-${tier}`} kind="primary" />
                <Caption variant="small">
                  <FormatDecimal value={targetCredits} />
                </Caption>
              </Group>
            </LoyaltyAccountTileCircularProgressBar>
            <Group direction="vertical" horizontalAlign="center" gap={2}>
              <BodyText variant="small" weight="bold">Status Credits</BodyText>
              <Group direction="horizontal" gap={4} verticalAlign="center">
                <BodyText variant="small"><FormatDecimal value={pendingPoints.statusCredits} /> pending</BodyText>
                <Tooltip description="Pending status credits may take up to 24hrs to appear" placement="bottom">
                  <InfoIconWrapper><LineInfoCircleIcon size={14} /></InfoIconWrapper>
                </Tooltip>
              </Group>
            </Group>
          </Group>

          <Group direction="vertical" horizontalAlign="center" gap={4}>
            <LoyaltyAccountTileCircularProgressBar
              progress={nightsProgress}
              tier={tier}
            >
              <Group direction="vertical" horizontalAlign="center" gap={2}>
                <BodyText variant="medium" weight="bold">
                  <FormatDecimal value={currentNights} />
                </BodyText>
                <TierDivider className={`tier-${tier}`} kind="primary" />
                <Caption variant="small">
                  <FormatDecimal value={targetNights} />
                </Caption>
              </Group>
            </LoyaltyAccountTileCircularProgressBar>
            <Group direction="vertical" horizontalAlign="center" gap={2}>
              <BodyText variant="small" weight="bold">Nights</BodyText>
              <Group direction="horizontal" gap={4} verticalAlign="center">
                <BodyText variant="small"><FormatDecimal value={pendingPoints.nights} /> pending</BodyText>
                <Tooltip description="Pending nights may take up to 24hrs to appear" placement="bottom">
                  <InfoIconWrapper><LineInfoCircleIcon size={14} /></InfoIconWrapper>
                </Tooltip>
              </Group>
            </Group>
          </Group>
        </ProgressBarsContainer>

        <VerticalSpacer gap={20}>
          {/* Tier card or congratulations banner */}
          {hasRetainedTier && (
            <LoyaltyDrawerCongratulationsBanner
              tier={tier}
              reviewDate={loyaltyAccountData.statusReviewDate}
            />
          )}
          {!hasRetainedTier && (
            <LoyaltyDrawerTierCard
              tier={unlockCardTier}
              isRetain={isRetainMode}
              creditsRemaining={creditsRemaining}
              nightsRemaining={nightsRemaining}
              expiryDate={loyaltyAccountData.statusReviewDate}
            />
          )}

          {/* Benefits section */}
          {validBenefitsDisplayInfo && validBenefitsDisplayInfo.length > 0 && (
            <VerticalSpacer gap={8}>
              <BodyText variant="small" weight="bold">Your top benefits</BodyText>
              {validBenefitsDisplayInfo.map(({ code, displayInfo }, index) => (
                <React.Fragment key={code}>
                  {index > 0 && <Divider kind="secondary" />}
                  <AccountDrawerBenefitHighlightItem
                    benefit={benefitsMap[code]}
                    tier={tier}
                    title={displayInfo.title}
                    icon={displayInfo.icon}
                  />
                </React.Fragment>
              ))}
            </VerticalSpacer>
          )}

          <Group direction="horizontal" horizontalAlign="end">
            <TextLink size="small">
              View membership
            </TextLink>
          </Group>
        </VerticalSpacer>
      </VerticalSpacer>
    </Container>
  </ProductPaletteProvider>
}

export default withMountGuard(() => {
  return useAppSelector(getIsLoyaltyEnabled)
})(AccountMenuLoyaltyHasAccountDetailsTile)
